/**
 * MetricAreaChart — a single-series area chart for a scalar metric over time
 * (request rate, error rate). Gradient fill under a curved top stroke, a "nice"
 * zero-based y-domain, and a hover crosshair with a focus dot + value tooltip.
 * Renders nothing for a series shorter than 2 points (nothing to trend yet).
 */
import { useId, type ReactNode } from "react";
import { scaleLinear } from "@visx/scale";
import { AreaClosed, LinePath } from "@visx/shape";
import { LinearGradient } from "@visx/gradient";
import { curveMonotoneX } from "@visx/curve";
import { ChartCanvas } from "./ChartCanvas.js";
import { CHART_HEIGHT, niceTop } from "./tokens.js";

export interface MetricAreaChartProps {
  readonly values: readonly number[];
  readonly color: string;
  /** Format a value for the tooltip (e.g. `42 req/s`, `1.3%`). */
  readonly format: (v: number) => string;
  readonly label: string;
  readonly height?: number;
}

export function MetricAreaChart({
  values,
  color,
  format,
  label,
  height = CHART_HEIGHT,
}: MetricAreaChartProps): ReactNode {
  const gradId = useId();
  if (values.length < 2) return null;
  const top = niceTop(Math.max(...values));
  return (
    <ChartCanvas
      count={values.length}
      height={height}
      ariaLabel={`${label} trend`}
      tooltip={(i) => format(values[i] ?? 0)}
    >
      {({ innerH, x, hover }) => {
        const y = scaleLinear<number>({ domain: [0, top], range: [innerH, 0] });
        const yv = (i: number) => y(values[i] ?? 0);
        return (
          <>
            <LinearGradient id={gradId} from={color} to={color} fromOpacity={0.35} toOpacity={0.02} />
            <AreaClosed
              data={values as number[]}
              x={(_d, i) => x(i)}
              y={(d) => y(d)}
              yScale={y}
              curve={curveMonotoneX}
              fill={`url(#${gradId})`}
              stroke="none"
            />
            <LinePath
              data={values as number[]}
              x={(_d, i) => x(i)}
              y={(d) => y(d)}
              curve={curveMonotoneX}
              stroke={color}
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
            {hover != null && (
              <circle cx={x(hover)} cy={yv(hover)} r={2.5} fill={color} stroke="#0f172a" strokeWidth={1} />
            )}
          </>
        );
      }}
    </ChartCanvas>
  );
}
